import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import styled, { css } from 'styled-components'
import Button from '../../../../common/src/components/Button'
import { Link } from '../../../../common/src/components/Link'
import Table, { TableColFn } from '../../../../common/src/components/Table'
import { H4, Spacer, BodyText, FixedWidthSpan, ColoredSpan } from '../../../../common/src/components/core'
import { Colors, Fonts } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import { dateMDYFmt } from '../../../../common/src/date'
import { lex } from '../../../../common/src/func'
import memoize from '../../../../common/src/memoize'
import {
  PostUnitMutation,
  FetchTaskMarketingQuery,
  FetchTaskMarketingQueryVariables,
  SuggestedPostAction,
  UnitStatus,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import JobsTable from '../components/JobsTable'
import { PollingQuery } from '../components/PollingQuery'

const pmUnitFragment = gql`
  fragment pmUnit on PmPostableUnit {
    id
    suggestedPostAction
    latestJobs {
      id
      jobName
      jobKey
      jobStatus
      timeCreated
      timeFinished
    }
    unit {
      displayName
      auditKeyJson
      status
      bedrooms
      bathrooms
      sqft
      minRentUSD
      maxRentUSD
      isPosted
      marketingTitle
      marketingDescription
      photoUrls
      pmUrl
    }
  }
`

const pmCampaignFragment = gql`
  fragment pmCampaignInfo on PmCampaign {
    id
    auditKeyJson
    bedrooms
    bathrooms
    sqft
    minRentUSD
    maxRentUSD
    isPosted
    marketingTitle
    marketingDescription
    availableOnDate
  }
`

export const fetchTaskMarketingQuery = gql`
  query FetchTaskMarketing($taskId: Int!) {
    task(taskId: $taskId) {
      id
      pmUnits {
        ...pmUnit
      }
      pmCampaigns {
        ...pmCampaignInfo
      }
    }
  }
  ${pmUnitFragment}
  ${pmCampaignFragment}
`

const postUnitMutation = gql`
  mutation PostUnit($jobName: PostJobName!, $auditKeyJson: String!) {
    postUnit(jobName: $jobName, auditKeyJson: $auditKeyJson) {
      id
      pmUnits {
        ...pmUnit
      }
    }
  }
  ${pmUnitFragment}
`

/**
 * A button to post or unpost marketing for a property or for a unit type.
 * The button will say "Post", "Posting...", "Unpost", etc.
 */
class MarketingPostButton extends React.PureComponent<
  {
    auditKeyJson: string
    isPosted: boolean
    onPosted?: () => void
  },
  { isPosting?: boolean }
> {
  state: { isPosting?: boolean } = {}

  private onClickBound = () => {
    const { isPosted, auditKeyJson } = this.props
    console.log(auditKeyJson)
    this.runPost(isPosted ? 'UNPOST' : 'POST', auditKeyJson)
  }

  private runPost = (jobName: 'POST' | 'UNPOST', auditKeyJson: string) => {
    this.setState({ isPosting: true }, () => {
      apolloWebClient
        .mutate<PostUnitMutation>({
          mutation: postUnitMutation,
          variables: { jobName, auditKeyJson },
        })
        .then(() => this.setState({ isPosting: false }))
        .then(this.props.onPosted)
        .catch(handleError)
    })
  }

  render() {
    const { isPosted } = this.props
    const { isPosting } = this.state

    const buttonText = isPosted ? 'Unpost' : 'Post'
    const isDisabled = isPosting

    return (
      <Button onClick={!isDisabled ? this.onClickBound : undefined} tabIndex={-1} disabled={isDisabled}>
        {buttonText}
      </Button>
    )
  }
}

type PmUnit = NonNullable<FetchTaskMarketingQuery['task']['pmUnits'][0]>
type PmCampaign = NonNullable<FetchTaskMarketingQuery['task']['pmCampaigns'][0]>

class MarketingUnitsImpl extends React.PureComponent<
  FetchTaskMarketingQueryVariables & QueryResult<FetchTaskMarketingQuery, FetchTaskMarketingQueryVariables>
> {
  state = {} as { selectedUnitId?: string }

  render() {
    const { data } = this.props
    const { selectedUnitId } = this.state

    if (data == null || data.task == null || data.task.pmUnits == null) {
      return null
    }
    const { pmUnits, pmCampaigns } = data.task

    const sortUnits = pmUnits.slice().sort(
      lex(
        u => (u.suggestedPostAction === SuggestedPostAction.NO_ACTION_NEEDED ? 'z' : u.suggestedPostAction),
        u => u.unit.displayName
      )
    )

    const selPmu = sortUnits.find(u => u.id === selectedUnitId) || sortUnits[0]
    const selName = pmUnits.length > 1 ? selPmu.unit.displayName : 'SFR'

    const unitRowComponent = ({ row, children }: { row: PmUnit; children: React.ReactNode }) => (
      <UnitRow
        selected={row === selPmu}
        onClick={memoize(this.handlers, { id: row.id }, () => () => this.select(row.id))}
        title={row.unit.displayName}
      >
        {children}
      </UnitRow>
    )

    return (
      <div>
        <div>
          <H4>Units</H4>
          <Spacer height={16} />
          <Table
            rows={sortUnits}
            columns={sortUnits.length > 1 ? this.colsMF : this.colsSFR}
            headers={this.headers}
            widths={this.widths}
            alignments={this.aligns}
            // eslint-disable-next-line react/jsx-no-bind
            dataRowComponent={unitRowComponent}
          />
          {sortUnits.length === 0 && <BodyText>No units</BodyText>}
          <Spacer height={32} />
          {selPmu && this.renderUnitDetails(selPmu, selName)}
        </div>
        <div>
          <H4>Campaigns</H4>
          <Spacer height={16} />
          <Table
            rows={pmCampaigns}
            columns={this.campaignCols}
            headers={this.campaignHeaders}
            widths={this.campaignWidths}
            alignments={this.campaignAligns}
          />
          <Spacer height={8} />
          {pmCampaigns.length === 0 && <BodyText>No campaigns</BodyText>}
          <Spacer height={32} />
        </div>
      </div>
    )
  }

  private renderUnitDetails(selPmu: PmUnit, selName: string) {
    return (
      <div>
        <H4>Marketing for {selName}</H4>
        <Spacer height={16} />
        <BodyText>
          <Link href={selPmu.unit.pmUrl}>» edit unit in APM</Link>
        </BodyText>
        <Spacer height={8} />
        <BodyText>
          {selPmu.unit.marketingDescription && 'Make sure description has no phone numbers or emails.'}
        </BodyText>
        {selPmu.unit.marketingDescription && <Spacer height={8} />}
        <MarketingText
          marketingTitle={selPmu.unit.marketingTitle}
          marketingDescription={selPmu.unit.marketingDescription}
        />
        <Spacer height={16} />
        <MarketingPostButton auditKeyJson={selPmu.unit.auditKeyJson} isPosted={selPmu.unit.isPosted!} />
        <Spacer height={16} />
        <JobsTable jobs={selPmu.latestJobs} />
        <Spacer height={32} />
      </div>
    )
  }

  private select(selectedUnitId: string) {
    this.setState({ selectedUnitId })
  }

  private handlers = {}

  private headers = ['Unit', 'Bed/ba', 'Rent', 'Title desc', 'Status', 'Posted', 'Action']

  private widths = ['72px', '48px', '48px', '64px', '114px', '48px', null]

  private aligns = ['left', 'left', 'right', 'right', 'right', 'right', 'right']

  private cols2Plus = [
    (u: PmUnit) => (
      <span>
        {dashIfNull(u, u.unit.bedrooms)} / {dashIfNull(u, u.unit.bathrooms)}
      </span>
    ),
    (u: PmUnit) => dashIfNull(u, u.unit.minRentUSD && `$${u.unit.minRentUSD}`),
    (u: PmUnit) => (
      <span>
        <FixedWidthSpan width={16} center>
          {dashIfNull(u, u.unit.marketingTitle && '✓')}
        </FixedWidthSpan>
        <FixedWidthSpan width={16} center>
          {dashIfNull(u, u.unit.marketingDescription && '✓')}
        </FixedWidthSpan>
        {/* <FixedWidthSpan width={16} center>
          dashIfNull(u, u.unit.photoUrls[0] && '✓'
        </FixedWidthSpan>*/}
      </span>
    ),
    (u: PmUnit) => <UnitStatusSpan status={u.unit.status} />,
    (u: PmUnit) => (u.unit.isPosted ? 'yes' : 'no'),
    (u: PmUnit) =>
      u.suggestedPostAction === SuggestedPostAction.NO_ACTION_NEEDED ? '✓' : u.suggestedPostAction.substring(0, 4),
  ] as TableColFn<PmUnit>[]

  private colsSFR = ([() => 'SFR'] as TableColFn<PmUnit>[]).concat(this.cols2Plus)

  private colsMF = ([(u: PmUnit) => u.unit.displayName] as TableColFn<PmUnit>[]).concat(this.cols2Plus)

  private campaignHeaders = ['Campaign', 'Bed/ba', 'Rent', 'Posted', 'Avail']

  private campaignWidths = ['114px', '48px', '48px', '48px', '54px']

  private campaignAligns = ['left', 'left', 'right', 'right', 'right']

  private campaignCols = [
    (c: PmCampaign) => c.marketingTitle,
    (c: PmCampaign) => (
      <span>
        {c.bedrooms} / {c.bathrooms}
      </span>
    ),
    (c: PmCampaign) => c.minRentUSD && `$${c.minRentUSD}`,
    (c: PmCampaign) => (c.isPosted ? 'yes' : 'no'),
    (c: PmCampaign) => (c.availableOnDate == null ? '-' : dateMDYFmt(c.availableOnDate, 'UTC')),
  ] as TableColFn<PmCampaign>[]
}

function dashIfNull(u: PmUnit, n?: number | string | null) {
  if (n || n === 0) {
    return n
  }
  if (u.suggestedPostAction == null) {
    return <span>&ndash;</span>
  }
  // Unit has suggested post action but is also missing info
  return <ColoredSpan color="coral">&ndash;</ColoredSpan>
}

export const UnitRow = styled.tr`
  ${({ selected }: { selected: boolean }) => css`
    font-variant-numeric: tabular-nums;
    cursor: ${selected ? 'default' : 'pointer'};
    color: ${selected ? Colors.white : 'inherit'};
    &:hover {
      background: ${Colors.black};
    }
  `};
`

function UnitStatusSpan({ status }: { status: UnitStatus }) {
  switch (status) {
    case UnitStatus.OCCUPIED_NO_NOTICE:
      return <span title={status}>occupied</span>
    case UnitStatus.OCCUPIED_RENTED_NOTICE:
      return <span title={status}>rented orn</span>
    case UnitStatus.OCCUPIED_UNRENTED_NOTICE:
      return <span title={status}>NOTICE</span>
    case UnitStatus.VACANT_RENTED:
      return <span title={status}>rented vr</span>
    case UnitStatus.VACANT_RENTED_READY:
      return <span title={status}>rented vrr</span>
    case UnitStatus.VACANT_UNRENTED:
      return <span title={status}>VACANT</span>
    case UnitStatus.VACANT_UNRENTED_READY:
      return <span title={status}>VACANT READY</span>
  }
}

function MarketingText({
  marketingTitle,
  marketingDescription,
}: {
  marketingTitle?: string | null
  marketingDescription?: string | null
}) {
  if (marketingTitle && marketingDescription) {
    return (
      <MarketingPre>
        {marketingTitle}
        {'\n\n'}
        {marketingDescription}
      </MarketingPre>
    )
  } else if (marketingTitle) {
    return (
      <MarketingPre>
        {marketingTitle}
        {'\n\n'}
        <ColoredSpan color="coral">Marketing description missing</ColoredSpan>
      </MarketingPre>
    )
  } else if (marketingDescription) {
    return (
      <MarketingPre>
        <ColoredSpan color="coral">Marketing title missing</ColoredSpan>
        {'\n\n'}
        {marketingDescription}
      </MarketingPre>
    )
  } else {
    return (
      <MarketingPre>
        <ColoredSpan color="coral">Marketing title and description missing</ColoredSpan>
      </MarketingPre>
    )
  }
}

const MarketingPre = styled.pre`
  font: 12px ${Fonts.mono};
  line-height: 1.5;
  color: #aaa;
  margin-bottom: 8px;
  padding: 8px 12px 4px 12px;
  border-radius: 4px;
  background-color: ${Colors.charcoal};
  white-space: pre-wrap;
`

export default function MarketingSection(props: FetchTaskMarketingQueryVariables) {
  return (
    <PollingQuery query={fetchTaskMarketingQuery} variables={props}>
      {(res: QueryResult<FetchTaskMarketingQuery, FetchTaskMarketingQueryVariables>) => (
        <MarketingUnitsImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}
